<template>
	<div class="card">
		<div class="card-body">
			<h4 class="mb-1">Filters</h4>
			<small class="text-muted">Choose how filters are displayed for Clients and Municipalities.</small>

			<div class="row mt-3">
				<div class="col-md-6">
					<label class="form-label">Clients filters</label>
					<select class="form-select" v-model="clientsSelected">
						<option value="inherit">Use system default ({{ clientsDefaultLabel }})</option>
						<option value="default">Default</option>
						<option value="modern">Modern</option>
					</select>
				</div>
				<div class="col-md-6">
					<label class="form-label">Municipalities filters</label>
					<select class="form-select" v-model="municipalitiesSelected">
						<option value="inherit">Use system default ({{ municipalitiesDefaultLabel }})</option>
						<option value="default">Default</option>
						<option value="modern">Modern</option>
					</select>
				</div>
			</div>

			<div class="mt-3">
				<button class="btn btn-primary" @click="save" :disabled="saving">Save</button>
				<span v-if="saving" class="ms-2 text-muted">Saving...</span>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import restApi from '@/api'

export default {
	name: 'AccountFilters',
	data() {
		return {
			clientsKey: { name: 'clientsFiltersUi', type: 'settings' },
			municipalitiesKey: { name: 'municipalitiesFiltersUi', type: 'settings' },
			clientsSettings: { default: 'default', overrides: {} },
			municipalitiesSettings: { default: 'default', overrides: {} },
			clientsSelected: 'inherit',
			municipalitiesSelected: 'inherit',
			saving: false,
		}
	},
	computed: {
		...mapState(['authUser']),
		clientsDefaultLabel() {
			return this.clientsSettings.default === 'modern' ? 'Modern' : 'Default'
		},
		municipalitiesDefaultLabel() {
			return this.municipalitiesSettings.default === 'modern' ? 'Modern' : 'Default'
		},
	},
	created() {
		this.getSettings()
	},
	methods: {
		async getSettings() {
			await Promise.all([this.loadSetting('clients'), this.loadSetting('municipalities')])
			this.applyOverrides()
		},
		async loadSetting(type) {
			const key = type === 'clients' ? this.clientsKey : this.municipalitiesKey
			try {
				const { data } = await restApi.get('/settings', { params: { name: key.name, type: key.type } })
				if (data && data.data) {
					const parsed = JSON.parse(data.data)
					if (!parsed.default) parsed.default = 'default'
					if (!parsed.overrides) parsed.overrides = {}
					if (type === 'clients') {
						this.clientsSettings = parsed
					} else {
						this.municipalitiesSettings = parsed
					}
				}
			} catch (error) {
				console.error(error)
			}
		},
		applyOverrides() {
			const clientsOverride = this.clientsSettings.overrides?.[this.authUser.id]
			this.clientsSelected = clientsOverride ? (typeof clientsOverride === 'string' ? clientsOverride : clientsOverride.mode) : 'inherit'

			const municipalitiesOverride = this.municipalitiesSettings.overrides?.[this.authUser.id]
			this.municipalitiesSelected = municipalitiesOverride
				? typeof municipalitiesOverride === 'string'
					? municipalitiesOverride
					: municipalitiesOverride.mode
				: 'inherit'
		},
		async save() {
			if (this.saving) return
			this.saving = true
			try {
				await Promise.all([this.saveSetting('clients'), this.saveSetting('municipalities')])
			} catch (error) {
				console.error(error)
			} finally {
				this.saving = false
			}
		},
		async saveSetting(type) {
			const isClients = type === 'clients'
			const key = isClients ? this.clientsKey : this.municipalitiesKey
			const settings = isClients ? { ...this.clientsSettings } : { ...this.municipalitiesSettings }
			const selected = isClients ? this.clientsSelected : this.municipalitiesSelected

			if (!settings.default) settings.default = 'default'
			if (!settings.overrides) settings.overrides = {}

			if (selected === 'inherit') {
				delete settings.overrides[this.authUser.id]
			} else {
				settings.overrides[this.authUser.id] = {
					mode: selected,
					name: this.authUser.name,
					email: this.authUser.email,
				}
			}

			const { data } = await restApi.post('/settings', {
				name: key.name,
				type: key.type,
				data: JSON.stringify(settings),
			})

			const parsed = JSON.parse(data.data)
			if (isClients) {
				this.clientsSettings = parsed
			} else {
				this.municipalitiesSettings = parsed
			}
		},
	},
}
</script>
